import { FC, useEffect } from 'react'
import { Manufacturer as ManufacturerModel } from '@/network/graphql.g'
import { requestThrow401 } from '@/utils/graphQl'
import { withBaseServerSideProps } from '@/HOC/withBaseServerSideProps'
import { getMainData } from '@/utils/mainDataHelper'
import { CacheType } from '@/utils/cache/pageDataCache'
import ManufacturerWrapper from '../components/organisms/manufacturer/manufacturer-wrapper'
import { useMhub } from '@/providers/mhubProvider'

type ManufacturerProps = {
  manufacturers: ManufacturerModel[]
}

const Manufacturer: FC<ManufacturerProps> = ({ manufacturers }) => {
  const { pushManufacturerPage } = useMhub()

  useEffect(() => {
    pushManufacturerPage()
  }, [pushManufacturerPage])

  const getGroup = (name: string) => {
    const firstChar = name[0].toUpperCase()
    if (/^[A-Z]$/.test(firstChar)) {
      return firstChar
    }
    return '#'
  }

  const groupedManufacturers = manufacturers.reduce((acc, manufacturer) => {
    const group = getGroup(manufacturer.name)
    if (!acc[group]) {
      acc[group] = []
    }
    acc[group].push(manufacturer)
    return acc
  }, {} as Record<string, ManufacturerModel[]>)

  const sortedGroupedManufacturers = Object.keys(groupedManufacturers)
    .sort((a, b) => {
      if (a === '#') {
        return -1
      }
      if (b === '#') {
        return 1
      }
      return a.localeCompare(b)
    })
    .reduce((acc, key) => {
      acc[key] = groupedManufacturers[key]
      return acc
    }, {} as Record<string, ManufacturerModel[]>)

  return (
    <ManufacturerWrapper
      manufacturersList={sortedGroupedManufacturers}
      manufacturers={manufacturers}
    />
  )
}

export default Manufacturer

export const getServerSideProps = withBaseServerSideProps(
  async ({
    sdk,
    withMainNavigation,
    withStore
  }): Promise<GetServerSideProps<ManufacturerProps>> => {
    const { success, response } = await requestThrow401({
      method: sdk.getManufacturers,
      variables: {
        withMainNavigation,
        withStore
      },
      successPredicate: (response) =>
        !!(response?.topManufacturers && response?.manufacturers),
      requestHeaders: {
        'x-cachable': '1'
      }
    })

    if (!success) {
      return {
        notFound: true
      }
    }

    return {
      props: {
        mainData: getMainData(response),
        seoTags: response?.seoTags,
        manufacturers: response?.manufacturers,
        disableDaktela: true
      }
    }
  },
  { cacheType: CacheType.Manufacturer }
)
